import React, { useState, useRef } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { ArrowLeft, ShieldCheck } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Password policy: at least 8 chars, 1 uppercase, 1 number (matches backend)
export const validatePasswordStrength = (password) => {
  if (!password || password.length < 8) return 'Password must be at least 8 characters';
  if (!/[A-Z]/.test(password)) return 'Password must contain at least one uppercase letter';
  if (!/[0-9]/.test(password)) return 'Password must contain at least one number';
  return '';
};

const PAID_PLANS = ['home_baker', 'pro_baker', 'bakery_business'];

export const AuthPage = () => {
  const [searchParams] = useSearchParams();
  const initialMode = searchParams.get('mode');
  const selectedPlan = searchParams.get('plan');
  const [isLogin, setIsLogin] = useState(initialMode !== 'signup');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [organisationName, setOrganisationName] = useState('');
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login, signup, twofaPending, verify2FA, cancel2FA } = useAuth();
  const navigate = useNavigate();

  // 2FA state
  const [twoFACode, setTwoFACode] = useState('');
  const [verifying2FA, setVerifying2FA] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!isLogin) {
      if (!agreedToTerms) {
        toast.error('Please agree to the Terms & Conditions and Privacy Policy');
        return;
      }
      const pwError = validatePasswordStrength(password);
      if (pwError) {
        toast.error(pwError);
        return;
      }
    }
    
    setLoading(true);

    try {
      if (isLogin) {
        const result = await login(email, password);
        if (result?.twofa_required) {
          toast.info(result.method === 'email' ? 'Verification code sent to your email' : 'Enter your authenticator code');
          setLoading(false);
          return;
        }
        toast.success('Welcome back!');
      } else {
        const result = await signup(email, password, organisationName, agreedToTerms);
        toast.success('Account created successfully!');
        // If a paid plan was chosen on the landing page, send them straight to checkout
        if (selectedPlan && PAID_PLANS.includes(selectedPlan)) {
          try {
            const token = result?.access_token || localStorage.getItem('token');
            const res = await axios.post(
              `${API}/payments/create-checkout`,
              { plan_id: selectedPlan, origin_url: window.location.origin, billing_interval: 'monthly' },
              { headers: { Authorization: `Bearer ${token}` } }
            );
            window.location.href = res.data.checkout_url;
            return;
          } catch (checkoutError) {
            toast.info('Account created. You can pick your plan anytime from Settings.');
          }
        }
      }
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  const handle2FAVerify = async (e) => {
    e.preventDefault();
    setVerifying2FA(true);
    try {
      await verify2FA(twoFACode);
      toast.success('Welcome back!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Verification failed');
    } finally {
      setVerifying2FA(false);
    }
  };

  const handleCancel2FA = () => {
    cancel2FA();
    setTwoFACode('');
  };

  // 2FA Verification Screen
  if (twofaPending) {
    return (
      <div className="min-h-screen flex">
        <div
          className="hidden lg:flex lg:w-1/2 bg-cover bg-center relative"
          style={{ backgroundImage: 'url(/hero-image.jpg)' }}
        >
          <div className="absolute inset-0 bg-gradient-to-br from-[#2C5F7C]/80 to-[#1a3a4d]/60" />
          <div className="relative z-10 flex flex-col justify-center px-16 text-white">
            <div className="flex items-center gap-4 mb-6">
              <img src="/costcraft-logo.jpg" alt="CostCraft" className="w-16 h-16 rounded-xl shadow-lg object-contain bg-white/10" />
              <h1 className="text-5xl font-bold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>CostCraft</h1>
            </div>
            <h2 className="text-3xl font-bold mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>Two-Factor Authentication</h2>
            <p className="text-lg text-white/90">One more step to keep your account secure.</p>
          </div>
        </div>
        <div className="flex-1 flex items-center justify-center p-8">
          <div className="w-full max-w-md">
            <div className="mb-8 text-center">
              <ShieldCheck className="w-12 h-12 text-[#2C5F7C] mx-auto mb-4" />
              <h2 className="text-2xl font-semibold mb-2 text-[#2C5F7C]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Verify your identity
              </h2>
              <p className="text-[#6B7280]">
                {twofaPending.method === 'email' 
                  ? 'Enter the 6-digit code sent to your email' 
                  : 'Enter the code from your authenticator app'}
              </p>
            </div>
            <form onSubmit={handle2FAVerify} className="space-y-5">
              <div>
                <Label htmlFor="twofa-code">Verification Code</Label>
                <Input
                  id="twofa-code"
                  data-testid="twofa-code-input"
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  value={twoFACode}
                  onChange={(e) => setTwoFACode(e.target.value.replace(/\D/g, ''))}
                  placeholder="000000"
                  required
                  autoFocus
                  className="mt-2 h-14 rounded-lg text-center text-2xl tracking-[0.5em] font-mono"
                />
              </div>
              <Button
                type="submit"
                data-testid="verify-2fa-button"
                disabled={verifying2FA || twoFACode.length < 6}
                className="w-full h-11 rounded-full bg-[#2C5F7C] hover:bg-[#1E4A60] text-white font-medium"
              >
                {verifying2FA ? 'Verifying...' : 'Verify'}
              </Button>
            </form>
            <button
              onClick={handleCancel2FA}
              className="mt-4 flex items-center justify-center w-full text-[#6B7280] hover:text-[#2C5F7C] transition-colors text-sm"
            >
              <ArrowLeft className="w-4 h-4 mr-1" />
              Back to login
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex">
      {/* Left side - Image/Branding */}
      <div
        className="hidden lg:flex lg:w-1/2 bg-cover bg-center relative"
        style={{ backgroundImage: 'url(/hero-image.jpg)' }}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-[#2C5F7C]/80 to-[#1a3a4d]/60" />
        <div className="relative z-10 flex flex-col justify-center px-16 text-white">
          <div className="flex items-center gap-4 mb-6">
            <img src="/costcraft-logo.jpg" alt="CostCraft" className="w-16 h-16 rounded-xl shadow-lg object-contain bg-white/10" />
            <h1 className="text-5xl font-bold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>CostCraft</h1>
          </div>
          <h2 className="text-3xl font-bold mb-4 leading-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Stop losing margin<br />without knowing it.
          </h2>
          <p className="text-lg text-white/90 leading-relaxed">
            Ingredient prices change.<br />
            One small increase can wipe out your profit.<br />
            <strong>CostCraft updates every recipe instantly</strong> so you always know your real numbers.
          </p>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <div className="mb-8">
            <h2 className="text-3xl font-semibold mb-2 text-[#2C5F7C]" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {isLogin ? 'Welcome back' : 'Get started'}
            </h2>
            <p className="text-[#6B7280]">
              {isLogin ? 'Sign in to your account' : 'Create your account'}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5" data-testid="auth-form">
            {!isLogin && (
              <div>
                <Label htmlFor="organisation">Business name</Label>
                <Input
                  id="organisation"
                  data-testid="organisation-input"
                  type="text"
                  value={organisationName}
                  onChange={(e) => setOrganisationName(e.target.value)}
                  placeholder="Your business name"
                  required
                  className="mt-2 h-11 rounded-lg"
                />
              </div>
            )}

            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                data-testid="email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@business.com"
                required
                className="mt-2 h-11 rounded-lg"
              />
            </div>

            <div>
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Password</Label>
                {isLogin && (
                  <Link 
                    to="/forgot-password" 
                    className="text-sm text-[#2C5F7C] hover:underline"
                    data-testid="forgot-password-link"
                  >
                    Forgot password?
                  </Link>
                )}
              </div>
              <Input
                id="password"
                data-testid="password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={isLogin ? 'Enter your password' : 'At least 8 characters'}
                required
                className="mt-2 h-11 rounded-lg"
              />
              {!isLogin && (
                <p className="text-xs text-[#6B7280] mt-1">Min 8 characters, 1 uppercase, 1 number</p>
              )}
            </div>

            {!isLogin && (
              <div className="flex items-start space-x-3">
                <Checkbox
                  id="terms"
                  data-testid="terms-checkbox"
                  checked={agreedToTerms}
                  onCheckedChange={setAgreedToTerms}
                  className="mt-1 rounded-[4px]"
                />
                <Label htmlFor="terms" className="text-sm text-[#6B7280] leading-relaxed cursor-pointer">
                  I agree to the{' '}
                  <Link to="/terms" target="_blank" className="text-[#2C5F7C] hover:underline">
                    Terms & Conditions
                  </Link>{' '}
                  and{' '}
                  <Link to="/terms#privacy" target="_blank" className="text-[#2C5F7C] hover:underline">
                    Privacy Policy
                  </Link>
                </Label>
              </div>
            )}

            <Button
              type="submit"
              data-testid="submit-button"
              disabled={loading}
              className="w-full h-11 rounded-full bg-[#2C5F7C] hover:bg-[#1E4A60] text-white font-medium transition-all duration-300"
            >
              {loading ? 'Please wait...' : isLogin ? 'Sign in' : 'Create account'}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <button
              type="button"
              data-testid="toggle-auth-mode"
              onClick={() => setIsLogin(!isLogin)}
              className="text-[#6B7280] hover:text-[#2C5F7C] transition-colors"
            >
              {isLogin ? "Don't have an account? Sign up" : 'Already have an account? Sign in'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
